import { API } from "@/app/api/BaseAPI";
import { Convert as ServerEntityConvert } from "@/app/entity/ServerEntity";
import { ServersEntity, Convert as ServersEntityConvert } from "@/app/entity/ServersEntity";

/**
 * A class for calling server api.
 *
 * The documentation of the api are below:
 * - [Get all servers of user](https://wisdomme.gitbook.io/ultikits-panel/get-info-1/get-all-servers)
 * - [Get server information](https://wisdomme.gitbook.io/ultikits-panel/server/get-server-info)
 * - [Add server](https://wisdomme.gitbook.io/ultikits-panel/server/untitled)
 * - [Update server](https://wisdomme.gitbook.io/ultikits-panel/server/update-server-info)
 * - [Delete server](https://wisdomme.gitbook.io/ultikits-panel/server/delete-server)
 *
 * You can use this api to get, add, update, delete server.
 *
 * This api requires authentication, so you need to ensure that the user is logged in before using it.
 *
 * It based on the BaseAPI, so you can use the BaseAPI to do more operations.
 *
 * @class ServerAPI
 *
 * @author: qianmo
 * @date:  2023-07-02 20:56
 * @version: 2.0.0
 * @since: 2.0.0
 */
export class ServerAPI {
  /**
   * The endpoint of server api, which is used to get all servers of user.
   */
  readonly serverPath = "/server/"
  /**
   * The endpoint of server api, which is used to add server.
   */
  readonly addPath = "/server/addserver"
  /**
   * The endpoint of server api, which is used to update server.
   */
  readonly updatePath = "/server/updateserver/"
  /**
   * The endpoint of server api, which is used to delete server.
   */
  readonly deletePath = "/server/delserver/"
  /**
   * The endpoint of server api, which is used to update server UUID by numeric ID.
   */
  readonly updateUuidPath = "/server/updateuuid/"

  /**
   * @function getAllServers
   *
   * Get all servers of user
   *
   * You can use this function to get all servers of user
   *
   * It will return a promise of ServersEntity which contains the server list.
   *
   * The server list is an array of ServerEntity.
   *
   * @example ``` typescript
   * getAllServers(1).then(
   *   (servers) => {
   *     console.log(servers);
   *     // do something
   *   }
   * )
   * ```
   *
   * @see ServersEntity
   * @see ServerEntity
   *
   * @param id user id
   *
   * @return Promise of ServersEntity
   */
  getAllServers(id: number | string): Promise<ServersEntity[]> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(`/user/${id}/allservers`)
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          // API返回格式: {"code": "200", "msg": "Success", "data": [...]}
          // 我们需要从response.data中提取data字段
          const serverData = response.data.data || response.data;
          resolve(ServersEntityConvert.toServersEntity(serverData));
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * @function getServer
   *
   * Get server by UUID
   *
   * You can use this function to get server by UUID (serverUuid)
   *
   * It will return a promise of ServerEntity which contains the server info.
   * @see ServerEntity
   *
   * @example ``` typescript
   * getServer("550e8400-e29b-41d4-a716-446655440000", 1).then(
   *   (server) => {
   *     console.log(server);
   *     // do something
   *   }
   * )
   * ```
   *
   * @param serverUuid server UUID (字符串格式)
   * @param ownerId owner id
   *
   * @return Promise of ServerEntity
   */
  getServer(serverUuid: string, ownerId: string | number): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(this.serverPath + serverUuid)  // 现在使用 serverUuid 而不是数字 ID
        .withMethod("POST")
        .withToken()
        .withParams({ ownerId })
        .request()
        .then((response) => {
          // 处理可能的嵌套data结构
          const serverData = response.data.data || response.data;
          resolve(ServerEntityConvert.toServerEntity(serverData));
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * @function addServer
   *
   * Add server
   *
   * You can use this function to add server
   *
   * The uuid is the server uuid, you can get it from the spigot server which installed UltiTools.
   *
   * 注意：根据新的API文档，所有参数都通过JSON请求体发送。
   *
   * @param serverName server name (必需)
   * @param ownerId owner id (必需)
   * @param serverDomain server domain (必需)
   * @param uuid server uuid (必需)
   * @param serverIp server ip (可选)
   * @param isActive whether to activate immediately (可选，默认false)
   *
   * @return Promise of response, if success, the response will be like this: `{"code": "200", "msg": "OK"}`
   */
  addServer(serverName: string, ownerId: number | string, serverDomain: string, uuid: string, serverIp = "", isActive = false): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(this.addPath)
        .withMethod("POST")
        .withToken()
        .withBody({
          serverName,
          ownerId: Number(ownerId),
          serverDomain,
          uuid,
          serverIp,
          isActive,
        })
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * Update server
   *
   * You can use this function to update server info
   * serverUuid is required, others are optional.
   * name, uuid, ip, active, port, ssl are optional, but you must provide at least one of them.
   * You can't update owner id.
   *
   * @param serverUuid server UUID (字符串格式)
   * @param id owner id
   * @param name server name
   * @param uuid server uuid
   * @param ip server ip
   * @param active server activation status
   * @param port server restful api port
   * @param ssl server restful api ssl status
   *
   * @return Promise of response, if success, the response will be like this: `{"code": 200, "msg": "Operate successfully!"}`
   */
  updateServer(serverUuid: string, id: number | string,
                        {name, uuid, ip, active, port, ssl}: Partial<{
                          name: string, uuid: string, ip: string, active: boolean, ssl: boolean, port: number }>)
    : Promise<any> {
    const api = new API();
    const params: any = {};

    params.ownerId = id;

    if (name !== undefined) params.serverName = name;
    if (uuid !== undefined) params.serverDomain = uuid;
    if (ip !== undefined) params.serverIp = ip;
    if (active !== undefined) params.isActive = active;
    if (port !== undefined) params.port = port;
    if (ssl !== undefined) params.ssl = ssl;

    return new Promise((resolve, reject) => {
      api
        .withPath(this.updatePath + serverUuid)  // 现在使用 serverUuid
        .withParams(params)
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * deleteServer
   *
   * You can use this function to delete server
   * It will return a promise of response, if success, the response will be like this: `{"code": 200, "msg": "Operate successfully!"}`
   *
   * @param id user id
   * @param serverUuid server UUID (字符串格式)
   *
   * @return Promise of response
   */
  deleteServer(id: number | string, serverUuid: string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(this.deletePath + serverUuid)  // 现在使用 serverUuid
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  /**
   * updateServerUuid
   *
   * Update server UUID by numeric server ID
   * This function uses the numeric server ID to update the UUID, allowing servers without UUID to be updated
   *
   * @param serverId server numeric ID
   * @param newUuid new UUID string
   * @param ownerId owner ID
   *
   * @return Promise of response, if success, the response will be like this: `{"code": "200", "msg": "OK"}`
   */
  updateServerUuid(serverId: number, newUuid: string, ownerId: number | string): Promise<any> {
    const api = new API();

    return new Promise((resolve, reject) => {
      api
        .withPath(this.updateUuidPath + serverId)
        .withParams({
          newUuid,
          ownerId,
        })
        .withMethod("POST")
        .withToken()
        .request()
        .then((response) => {
          resolve(response.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }
}
